/**
 * Feb 2026 · Effort/hours formatter — one source of truth for every UI
 * surface that renders decimal hours (Daily Plan, Timesheet reports,
 * task hours, People analytics, Compensation grid).
 *
 * Bug we're solving: Day and Month totals used to disagree because
 * each surface rounded values independently → sum(round(x_i)) !=
 * round(sum(x_i)). By exposing a single formatter that:
 *   • Only rounds at the DISPLAY boundary
 *   • Reads the digit count from the workspace's `timesheet.effort_rounding_digits`
 * we guarantee every surface renders consistently.
 *
 * Usage:
 *   const { formatHours, digits } = useEffortFormat();
 *   formatHours(entry.hours)        → "8.00"
 *   formatHours(sum, { unit: true }) → "40.00h"
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const DEFAULT_DIGITS = 2;

const EffortFormatContext = createContext({
  digits: DEFAULT_DIGITS,
  setDigits: () => {},
  isLoaded: false,
});

/**
 * Formats a number of hours to the workspace's configured decimal
 * digits. Accepts a null / NaN / undefined and returns "0.00" (or
 * whatever the current digits config produces).
 *
 * NOTE: This function does not sum. It only formats. Callers are
 * responsible for summing UNROUNDED values first, then formatting the
 * total once — that's the whole point.
 */
export function formatHoursWith(value, digits = DEFAULT_DIGITS, opts = {}) {
  const n = Number.isFinite(Number(value)) ? Number(value) : 0;
  const rounded = n.toFixed(Math.max(0, Math.min(3, digits)));
  return opts.unit ? `${rounded}h` : rounded;
}

export function EffortFormatProvider({ children }) {
  const { activeWorkspaceId } = useAuth();
  const [digits, setDigits] = useState(DEFAULT_DIGITS);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) { setDigits(DEFAULT_DIGITS); setIsLoaded(true); return; }
    let cancelled = false;
    setIsLoaded(false);
    api.get(`/workspaces/${activeWorkspaceId}/settings/timesheet`)
       .then((r) => {
         if (cancelled) return;
         const v = r?.data?.effort_rounding_digits;
         if (typeof v === "number" && v >= 0 && v <= 3) setDigits(v);
         else setDigits(DEFAULT_DIGITS);
       })
       .catch(() => { if (!cancelled) setDigits(DEFAULT_DIGITS); })
       .finally(() => { if (!cancelled) setIsLoaded(true); });
    return () => { cancelled = true; };
  }, [activeWorkspaceId]);

  const value = useMemo(() => ({ digits, setDigits, isLoaded }), [digits, isLoaded]);
  return <EffortFormatContext.Provider value={value}>{children}</EffortFormatContext.Provider>;
}

export function useEffortFormat() {
  const ctx = useContext(EffortFormatContext);
  const formatHours = useMemo(
    () => (v, opts = {}) => formatHoursWith(v, ctx.digits, opts),
    [ctx.digits],
  );
  return {
    digits: ctx.digits,
    setDigits: ctx.setDigits,
    isLoaded: ctx.isLoaded,
    formatHours,
  };
}
